import { IAlertProps } from "@components/alert/Alert.types";
import { Button, ButtonGroup } from "@components/button";
import { WithConfirmationDialog, withConfirmationDialog } from "@components/dialog/withConfirmationDialog";
import { IReadOnlyListItem } from "@components/readOnlyList/ReadOnlyListItem";
import { ISelectionWithEditableAmountCustomTableData } from "@components/smart/selectionTableView/SelectionTableView.utils";
import SelectionTableViewDialog, { IProps as ISelectionTableViewDialogProps } from "@components/smart/selectionTableView/SelectionTableViewDialog";
import { ActionType, addNewLineItem, ISmartFastEntriesActionEvent } from "@components/smart/smartFastEntryList";
import { ISmartFieldBlur, ISmartFieldChange } from "@components/smart/smartField/SmartField";
import SmartFormDeleteButton from "@components/smart/smartFormDeleteButton/SmartFormDeleteButton";
import { TSmartODataTableStorage } from "@components/smart/smartTable/SmartODataTableBase";
import BindingContext, { createPath, IEntity } from "@odata/BindingContext";
import { getExpandedEnumProps, setBoundValue } from "@odata/Data.utils";
import { getEntitySetByDocumentType } from "@odata/EntityTypes";
import {
    EntitySetName,
    EntityTypeName,
    IDocumentEntity,
    IPrBenefitEntity,
    IPrEmploymentTypeEntity,
    IPrExtraSalaryEntity,
    IPrPayrollPeriodEntity,
    IPrPayslipEmploymentBenefitEntity,
    IPrPayslipEmploymentEntity,
    IPrPayslipEmploymentExtraSalaryEntity,
    IPrPayslipEmploymentSalaryComponentEntity,
    IPrPayslipEntity,
    IPrPayslipLiabilityReceivableEntity,
    IPrSalaryComponentEntity,
    PrBenefitEntity,
    PrBenefitTemporalCurrentEntity,
    PrBenefitTypeEntity,
    PrEmploymentEntity,
    PrExtraSalaryEntity,
    PrExtraSalaryTemporalCurrentEntity,
    PrExtraSalaryTypeEntity,
    PrOtherBenefitCategoryEntity,
    PrOtherBenefitTaxAndContributionsApplicabilityEntity,
    PrOtherBenefitTaxDeducibilityEntity,
    PrPayslipDeductionEntity,
    PrPayslipEmploymentBenefitEntity,
    PrPayslipEmploymentEntity,
    PrPayslipEmploymentExtraSalaryEntity,
    PrPayslipEmploymentRetirementSavingEntity,
    PrPayslipEmploymentSalaryComponentEntity,
    PrPayslipEntity,
    PrPayslipLiabilityReceivableEntity,
    PrSalaryComponentEntity
} from "@odata/GeneratedEntityTypes";
import {
    DocumentTypeCode,
    PrEmploymentTypeCode,
    PrExtraSalaryTypeCode,
    PrSocialInsuranceDiscountIneligibilityReasonCode
} from "@odata/GeneratedEnums";
import { getEnumDisplayValue } from "@odata/GeneratedEnums.utils";
import { getCurrentRangeIndex, TTemporal } from "@odata/TemporalUtils";
import { getAgendaCountryCode } from "@pages/companies/Company.shared.utils";
import { IGetDefinition } from "@pages/PageUtils";
import { getPublicHolidays } from "@pages/payroll/attendance/Attendance.utils";
import { getDefinitionsFactory as getPickBenefitDefinitionsFactory } from "@pages/payroll/benefits/Benefit.def";
import {
    handlePayslipBenefitBlur,
    handleTaxAndContributionsApplicabilityChange,
} from "@pages/payroll/benefits/Benefit.utils";
import PickBenefitTableView from "@pages/payroll/benefits/PickBenefitTableView";
import { getDefinitionsFactory as getPickExtraSalaryDefinitionsFactory } from "@pages/payroll/extraSalary/ExtraSalaryDef";
import PickExtraSalaryTableView from "@pages/payroll/extraSalary/PickExtraSalaryTableView";
import { IFormTableListSection } from "@pages/payroll/payslip/formTableList/FormTableList";
import {
    attendanceOverviewPath,
    BenefitsGroupId,
    extraSalariesGroupId,
    PayslipFormAction,
    RetirementsGroupId,
    salaryComponentsGroupId
} from "@pages/payroll/payslip/Payslip.def";
import { DetectedChangesSubtitle } from "@pages/payroll/payslip/Payslip.styles";
import {
    currentEmploymentPath,
    DialogType,
    getCurrentEmploymentBc,
    getPayslipEmploymentTabId,
    getPeriodForPayslip,
    hasOpenAttendance,
    hasUnseizableOverridePath,
    renderDetectedChangeInfo,
    resetPayslipToOriginal,
    setCurrentPayslipEmploymentTabId,
    UnseizableTotalAmount,
} from "@pages/payroll/payslip/Payslip.utils";
import { getDefinitionsFactory } from "@pages/payroll/payslip/PayslipPairDocumentTableDef";
import PayslipPairDocumentTableView from "@pages/payroll/payslip/PayslipPairDocumentTableView";
import SendPayslipByEmailDialog from "@pages/payroll/payslip/SendPayslipByEmailDialog";
import {
    handleRetirementSavingsLineItemsChange,
    setupRetirementSavingsCompanyId,
} from "@pages/payroll/retirementSavingsCompany/RetirementSavings.utils";
import PickSalaryComponentTableView from "@pages/payroll/salaryComponent/PickSalaryComponentTableView";
import { getDefinitionsFactory as getPickSalaryComponentDefinitionsFactory } from "@pages/payroll/salaryComponent/SalaryComponentDef";
import { getCompanyCurrency } from "@utils/CompanyUtils";
import { isNotDefined } from "@utils/general";
import { memoizeOne } from "@utils/memoizeOne";
import { universalPersonNameFormatter } from "@utils/string";
import i18next from "i18next";
import { cloneDeep } from "lodash";
import React from "react";

import { withPermissionContext } from "../../../contexts/permissionContext/withPermissionContext";
import { Status } from "../../../enums";
import { formatCurrency } from "../../../types/Currency";
import DateType, { DateFormat, getUtcDateBy, getUtcDayjs } from "../../../types/Date";
import { FormStorage } from "../../../views/formView/FormStorage";
import { FormViewForExtend, IFormViewProps } from "../../../views/formView/FormView";

export interface IPrPayslipFormStorageCustomData extends ISelectionWithEditableAmountCustomTableData {
    editableSections: IFormTableListSection[];
    CurrentPeriodWorkDays?: number;
    CurrentPeriodHolidays?: number;
    currentPeriod?: IPrPayrollPeriodEntity;
    showEmailDialog?: boolean;
}

export const SocialInsuranceDiscountTypeLocalPath = BindingContext.localContext("SocialInsuranceDiscountType");

class PayslipFormView extends FormViewForExtend<IPrPayslipEntity, IFormViewProps<IPrPayslipEntity, IPrPayslipFormStorageCustomData> & WithConfirmationDialog & { isOverview?: boolean }> {
    _dialogType: DialogType;

    _overrideColumnPaths = [
        PrPayslipEntity.IsDeduceMinimumHealthInsurance,
        PrPayslipEntity.IsMinimumHealthInsurancePayedByEmployer,
        PrPayslipEntity.UnseizableTotalAmount,
        PrPayslipEntity.IsUnreducedMultipleDistrainment,
        createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.AverageSalary),
        createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.SalaryComponents, PrPayslipEmploymentSalaryComponentEntity.Amount),
        createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.SalaryComponents, PrPayslipEmploymentSalaryComponentEntity.Percentage),
        createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.ExtraSalaries, PrPayslipEmploymentExtraSalaryEntity.Value),
        createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.ExtraSalaries, PrPayslipEmploymentExtraSalaryEntity.Length),
        createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.Benefits, PrPayslipEmploymentBenefitEntity.Amount),
        createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.Benefits, PrPayslipEmploymentBenefitEntity.AmountEmployee),
        createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.Benefits, PrPayslipEmploymentBenefitEntity.AmountEmployer),
        createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.Benefits, PrPayslipEmploymentBenefitEntity.Length),
        createPath(PrPayslipEntity.Deductions, PrPayslipDeductionEntity.Amount),
        createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.RetirementSavings, PrPayslipEmploymentRetirementSavingEntity.Amount),
        createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.RetirementSavings, PrPayslipEmploymentRetirementSavingEntity.Percentage),
        createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.RetirementSavings, PrPayslipEmploymentRetirementSavingEntity.SymbolVariable),
        createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.RetirementSavings, PrPayslipEmploymentRetirementSavingEntity.SymbolSpecific),
        createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.RetirementSavings, PrPayslipEmploymentRetirementSavingEntity.SymbolConstant),
        createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.RetirementSavings, PrPayslipEmploymentRetirementSavingEntity.RemittanceInformation),
    ];

    get isCustomerOverview(): boolean {
        return this.props.isOverview;
    }

    shouldHideButtons(): boolean {
        return super.shouldHideButtons() && !this.isCustomerOverview;
    }

    get currentPeriodDayjs() {
        // return first day of the month - note, do not use actual day,
        // as it may behave differently according to current day in month, e.g. 31.1. -> February
        return getUtcDayjs().set("date", 1).set("month", this.entity.Month - 1).set("year", this.entity.Year);
    }

    get currentEmploymentBc(): BindingContext {
        return getCurrentEmploymentBc(this.props.storage);
    }

    get currentEmployment(): IPrPayslipEmploymentEntity {
        return this.props.storage.getValue(this.currentEmploymentBc);
    }

    getFormSubtitle = (): string => {
        const date = this.currentPeriodDayjs.toDate();
        return `/${DateType.format(date, DateFormat.monthAndYear)}/`;
    };

    get hasOpenAttendance(): boolean {
        return hasOpenAttendance({ storage: this.props.storage });
    }

    shouldDisableSaveButton = (): boolean => {
        return this.props.storage.isDisabled;
    };

    async onAfterLoad(): Promise<void> {
        if (this.entity.Employments?.length) {
            const { storage } = this.props;
            const currentTab = storage.getValueByPath(currentEmploymentPath);
            const currentTabEmployment = this.entity.Employments.find(e => getPayslipEmploymentTabId(e) === currentTab);
            if (!currentTabEmployment) {
                setCurrentPayslipEmploymentTabId(this.props.storage, getPayslipEmploymentTabId(this.entity.Employments[0]));
            }
        }

        if (this.entity.MasterDataChanges?.length) {
            this.infoAlert = {
                title: i18next.t("Payslip:Form.MasterDataChangesAlertTitle"),
                subTitle: <>
                    {i18next.t("Payslip:Form.MasterDataChangesAlertMessage")}
                    <DetectedChangesSubtitle>{i18next.t("Payslip:Form.MasterDataChangesAlertSubtitle")}</DetectedChangesSubtitle>
                    {this.entity.MasterDataChanges.map(change => renderDetectedChangeInfo(change, this.props.storage))}
                </>,
                status: Status.Warning
            };
        } else {
            this.infoAlert = null;
        }

        // copy some default values from original column to override column
        this.copyValuesToOverrideColumn(this._overrideColumnPaths);
        this.setupSocialInsuranceDiscountTypeProps();

        this.entity.Employments.forEach((entity: IPrPayslipEmploymentEntity) => {
            setupRetirementSavingsCompanyId(entity);
        });

        this.setupPayslipDeductions();

        await Promise.all([this.fetchCurrentPeriod(), this.calculatePeriodInfo()]);

        return super.onAfterLoad();
    }

    async fetchCurrentPeriod() {
        const { storage } = this.props;
        const periods = await getPeriodForPayslip(storage.oData, storage.data.entity);
        const currentPeriod = periods?.[0];
        storage.setCustomData({ currentPeriod });
    }

    setupPayslipDeductions(): void {
        const { storage } = this.props;
        const payslip = storage.data.origEntity as IPrPayslipEntity;
        const type = !!payslip.UnseizableTotalAmountOverride ? UnseizableTotalAmount.Own
            : UnseizableTotalAmount.Calculated;
        storage.setValueByPath(hasUnseizableOverridePath, type);
    }

    handleHasUnseizableOverrideChange = (e: ISmartFieldChange): void => {
        const { storage } = this.props;
        if (e.bindingContext.getPath() === hasUnseizableOverridePath && e.triggerAdditionalTasks) {
            const val = e.value === UnseizableTotalAmount.Calculated ? null
                : storage.data.entity.UnseizableTotalAmount;
            storage.setValueByPath(PrPayslipEntity.UnseizableTotalAmountOverride, val);
        }
    }

    setupSocialInsuranceDiscountTypeProps(): void {
        const { storage } = this.props;
        const payslip = this.entity;
        // set override property for social insurance discount from the reason codelist
        if (isNotDefined(payslip.HasEmployerSocialInsuranceDiscountOverride)) {
            storage.setValueByPath(PrPayslipEntity.HasEmployerSocialInsuranceDiscountOverride, !payslip.SocialInsuranceDiscountIneligibilityReasonCode);
        }
        if (payslip.SocialInsuranceDiscountIneligibilityReasonCode !== PrSocialInsuranceDiscountIneligibilityReasonCode.DiscountReasonIsNotSetOnEmployee) {
            // find current reason for social insurance discount eligibility and set it to local context for easier access in form
            const payslipDate = getUtcDateBy(payslip.Year, payslip.Month);
            const currentRangeIdx = getCurrentRangeIndex(payslip.Employee.TemporalPropertyBag as TTemporal[], payslipDate);
            const currentRange = payslip.Employee.TemporalPropertyBag[currentRangeIdx];
            const reason = getEnumDisplayValue(EntityTypeName.PrSocialInsuranceDiscountType, currentRange.PrSocialInsuranceDiscountTypeCode);
            storage.setValueByPath(SocialInsuranceDiscountTypeLocalPath, reason);
        }
    }

    async calculatePeriodInfo(): Promise<void> {
        const { storage } = this.props;
        const companyCountry = getAgendaCountryCode(storage.context);
        const publicHolidays = await getPublicHolidays(companyCountry, this.entity.Year);
        const current = this.currentPeriodDayjs.startOf("month");
        const lastMonthDay = current.clone().endOf("month");
        let workDays = 0;
        let holidays = 0;
        let day = current;
        while (day.isBefore(lastMonthDay)) {
            if (day.day() !== 0 && day.day() !== 6) {
                const currentDay = day;
                const isHoliday = publicHolidays.find(h => h.isSame(currentDay, "day"));
                if (isHoliday) {
                    holidays++;
                } else {
                    workDays++;
                }
            }
            day = day.add(1, "day");
        }

        (storage as FormStorage<IPrPayslipEntity, IPrPayslipFormStorageCustomData>).setCustomData({
            CurrentPeriodWorkDays: workDays,
            CurrentPeriodHolidays: holidays
        });
    }

    copyValuesToOverrideColumn(paths: string[]): void {
        const { storage } = this.props;
        BindingContext.each(storage.data.entity, storage.data.bindingContext, (value, bindingContext) => {
            if (paths.includes(bindingContext.getNavigationPath(true))) {
                // should be copied
                const prop = bindingContext.getPath(true);
                const overrideBc = bindingContext.getParent().navigate(`${prop}Override`);
                const currentVal = storage.getValue(overrideBc);
                if (isNotDefined(currentVal)) {
                    storage.setValue(overrideBc, value);
                }
            }
            return true;
        });
    }

    clearNotOverriddenFields(paths: string[], data: IPrPayslipEntity): IPrPayslipEntity {
        const { storage } = this.props;
        BindingContext.each(data, storage.data.bindingContext, (originalVal, bindingContext) => {
            if (paths.includes(bindingContext.getNavigationPath(true))) {
                const prop = bindingContext.getPath(true);
                const overrideBc = bindingContext.getParent().navigate(`${prop}Override`);
                const currentVal = storage.getValue(overrideBc);
                if (currentVal === originalVal) {
                    setBoundValue({
                        data,
                        bindingContext: overrideBc,
                        newValue: null,
                        preventCloning: true,
                        dataBindingContext: storage.data.bindingContext
                    });
                }
            }
            return true;
        });
        return data;
    }

    // special change event from Attendance overview field
    // -> refresh whole form data if all Attendances are closed and reload current table row
    handleAttendanceChange = async (): Promise<void> => {
        const { storage } = this.props;
        // always refresh table row to update data
        this.props.onTableRefreshNeeded?.({ refreshRowOnly: true });
        // if all are closed reloads form data to get new BE calculations, etc...
        const currentTab = storage.getValueByPath(currentEmploymentPath);
        await storage.loadDataAfterSave({ defaultData: { [currentEmploymentPath]: currentTab }});
    };

    handleChange(e: ISmartFieldChange): void {
        const { storage } = this.props;
        // keep first
        if (e.bindingContext.getPath() === attendanceOverviewPath) {
            this.handleAttendanceChange();
            // do not process the change further
            return;
        }

        this.handleHasUnseizableOverrideChange(e);

        storage.handleChange(e);
        const shouldUpdate = e.triggerAdditionalTasks !== false;
        storage.refreshFields(shouldUpdate);
    }

    async handleBlur(args: ISmartFieldBlur): Promise<void> {
        const { storage } = this.props;
        handlePayslipBenefitBlur(storage, args, "Override");
        return super.handleBlur(args);
    }

    async handleCustomHeaderAction(actionId: string): Promise<void> {
        switch (actionId) {
            case PayslipFormAction.ConnectReceivables:
            case PayslipFormAction.ConnectLiabilities:
                const dialogType = actionId === PayslipFormAction.ConnectReceivables
                    ? DialogType.Receivables
                    : DialogType.Liabilities;
                // check if form is changed, if so, ask user what to do
                this._dialogType = dialogType;
                this.forceUpdate();
                break;
            case PayslipFormAction.SendPayslipEmail:
                this.props.storage.setCustomData({
                    showEmailDialog: true
                });
                this.forceUpdate();
                break;
            case PayslipFormAction.PastCorrections:
                window.alert("Not implemented yet...");
                break;
            default:
                return super.handleCustomHeaderAction(actionId);
        }
    }

    handleLineItemsChange(e: ISmartFieldChange): void {
        const { storage } = this.props;
        storage.handleLineItemsChange(e);

        const isSalaryComponentType = e.bindingContext.getParent()?.getPath(true) === PrPayslipEmploymentEntity.SalaryComponents
            && e.bindingContext.getPath() === PrPayslipEmploymentSalaryComponentEntity.Type;
        const isExtraSalaryComputationType = e.bindingContext.getParent()?.getPath(true) === PrPayslipEmploymentEntity.ExtraSalaries
            && e.bindingContext.getPath() === PrPayslipEmploymentExtraSalaryEntity.ComputationType;
        const isBenefitType = e.bindingContext.getParent()?.getPath(true) === PrPayslipEmploymentEntity.Benefits
            && e.bindingContext.getPath() === PrPayslipEmploymentBenefitEntity.Type;

        if(isExtraSalaryComputationType) {
            storage.clearValue(e.bindingContext.getParent().navigate(PrPayslipEmploymentExtraSalaryEntity.LengthOverride));
        }

        if (isSalaryComponentType || isExtraSalaryComputationType || isBenefitType) {
            // refresh whole group to show fields based on the selected type
            storage.addActiveGroup(e.bindingContext);
        }

        // NOTE: Add handler also to BulkAddPayslipItemsFormView
        handleRetirementSavingsLineItemsChange(e, storage);
        handleTaxAndContributionsApplicabilityChange(storage, e);

        const shouldUpdate = e.triggerAdditionalTasks !== false;
        storage.refreshFields(shouldUpdate);
    }

    handleLineItemsAction = (args: ISmartFastEntriesActionEvent<IPrPayslipEmploymentExtraSalaryEntity | IPrPayslipEmploymentSalaryComponentEntity>): void => {
        if (args.actionType === ActionType.Custom) {
            switch (args.customActionType) {
                case DialogType.SalaryComponent:
                case DialogType.ExtraSalary:
                case DialogType.Benefits:
                    this._dialogType = args.customActionType as DialogType;
                    break;

                default:
                    throw new Error(`Unknown custom actionType ${args.customActionType}`);
            }
            this.forceUpdate();
        } else {
            const isExtraSalary = args.groupId === extraSalariesGroupId;
            const isOneTimeItem = [extraSalariesGroupId, salaryComponentsGroupId, BenefitsGroupId, RetirementsGroupId].includes(args.groupId);
            if (args.actionType === ActionType.Add && isOneTimeItem) {
                args.affectedItems.forEach((item: IEntity) => {
                    item.IsOneTime = true;
                    if (isExtraSalary) {
                        const extra = item as IPrPayslipEmploymentExtraSalaryEntity;
                        extra.TypeCode = PrExtraSalaryTypeCode.Custom;
                        extra.Type = { Code: PrExtraSalaryTypeCode.Custom };
                    }
                });
            }

            this.props.storage.handleLineItemsAction(args);
            this.props.storage.refresh();
        }
    };

    handleExtraSalarySelection = async (_: BindingContext[], entities: IPrExtraSalaryEntity[]): Promise<void> => {
        const { storage } = this.props;
        const { oData } = storage;

        const extraSalaryBc = this.currentEmploymentBc
            .navigate(PrPayslipEmploymentEntity.ExtraSalaries);
        const ids = entities.map(e => e.Id);

        const query = oData.getEntitySetWrapper(EntitySetName.PrExtraSalaries).query()
            .select(PrExtraSalaryEntity.Id)
            .expand(PrExtraSalaryEntity.Type, (q) =>
                q.select(PrExtraSalaryTypeEntity.OriginCode))
            .expand(PrExtraSalaryEntity.CurrentTemporalPropertyBag, (q) =>
                q.select(PrExtraSalaryTemporalCurrentEntity.Name, PrExtraSalaryTemporalCurrentEntity.ComputationTypeCode, PrExtraSalaryTemporalCurrentEntity.Value))
            .filter(`Id in (${ids.join(",")})`);

        const extraSalaries = (await query.fetchData<IPrExtraSalaryEntity[]>()).value;

        extraSalaries.forEach((entity: IPrExtraSalaryEntity) => {
            const { Id, Value, ...PassProps } = entity.CurrentTemporalPropertyBag;
            const newExtraSalary: Partial<IPrPayslipEmploymentExtraSalaryEntity> = {
                ...PassProps,
                ...getExpandedEnumProps(EntityTypeName.PrExtraSalaryType, entity.Type?.Code, "Type", entity.Type),
                ...getExpandedEnumProps(EntityTypeName.PrExtraSalaryComputationType, entity.CurrentTemporalPropertyBag.ComputationTypeCode, "ComputationType"),
                ValueOverride: Value,
                IsOneTime: true,
            };
            const path = extraSalaryBc.getNavigationPath(false);
            addNewLineItem(storage, path, extraSalariesGroupId, [], newExtraSalary);
        });

        storage.refresh();
    };

    handleBenefitSelection = async (_: BindingContext[], entities: IPrBenefitEntity[]): Promise<void> => {
        const { storage } = this.props;
        const { oData } = storage;

        const benefitsBc = this.currentEmploymentBc
            .navigate(PrPayslipEmploymentEntity.Benefits);
        const ids = entities.map(e => e.Id);

        const query = oData.getEntitySetWrapper(EntitySetName.PrBenefits).query()
            .select(PrBenefitEntity.Id)
            .expand(PrExtraSalaryEntity.Type, (q) => q.select(PrBenefitTypeEntity.Code))
            .expand(PrBenefitEntity.CurrentTemporalPropertyBag, (q) =>
                q.select(PrBenefitTemporalCurrentEntity.Amount, PrBenefitTemporalCurrentEntity.AmountEmployee, PrBenefitTemporalCurrentEntity.AmountEmployer, PrBenefitTemporalCurrentEntity.Name)
                    .expand(PrBenefitTemporalCurrentEntity.OtherCategory, (subQ) => subQ.select(PrOtherBenefitCategoryEntity.Code, PrOtherBenefitCategoryEntity.Name))
                    .expand(PrBenefitTemporalCurrentEntity.TaxDeducibility, (subQ) => subQ.select(PrOtherBenefitTaxDeducibilityEntity.Code, PrOtherBenefitTaxDeducibilityEntity.Name))
                    .expand(PrBenefitTemporalCurrentEntity.TaxAndContributionsApplicability, (subQ) => subQ.select(PrOtherBenefitTaxAndContributionsApplicabilityEntity.Code, PrOtherBenefitTaxAndContributionsApplicabilityEntity.Name))
            )
            .filter(`Id in (${ids.join(",")})`);

        const benefits = (await query.fetchData<IPrBenefitEntity[]>()).value;

        benefits.forEach((entity: IPrBenefitEntity) => {
            const {
                Id,
                Amount,
                AmountEmployee,
                AmountEmployer,
                ...PassProps
            } = entity.CurrentTemporalPropertyBag;
            const newBenefit: Partial<IPrPayslipEmploymentBenefitEntity> = {
                ...PassProps,
                ...getExpandedEnumProps(EntityTypeName.PrBenefitType, entity.Type?.Code, "Type", entity.Type),
                AmountOverride: Amount,
                AmountEmployeeOverride: AmountEmployee,
                AmountEmployerOverride: AmountEmployer,
                IsOneTime: true,
            };
            const path = benefitsBc.getNavigationPath(false);
            addNewLineItem(storage, path, BenefitsGroupId, [], newBenefit);
        });

        storage.refresh();
    };

    handleSalaryComponentSelection = async (_: BindingContext[], entities: IPrSalaryComponentEntity[]): Promise<void> => {
        const { storage } = this.props;
        const { oData } = storage;

        const salaryComponentsBc = this.currentEmploymentBc
            .navigate(PrPayslipEmploymentEntity.SalaryComponents);
        const ids = entities.map(e => e.Id);

        const query = oData.getEntitySetWrapper(EntitySetName.PrSalaryComponents).query()
            .select(PrSalaryComponentEntity.Id, PrSalaryComponentEntity.TypeCode, PrSalaryComponentEntity.RegularSalaryTypeCode)
            .expand(PrSalaryComponentEntity.CurrentTemporalPropertyBag)
            .filter(`Id in (${ids.join(",")})`);

        const salaryComponents = (await query.fetchData<IPrSalaryComponentEntity[]>()).value;
        salaryComponents.forEach((entity: IPrSalaryComponentEntity) => {
            const { Id, Percentage, Amount, IsBasicSalary, ...PassProps } = entity.CurrentTemporalPropertyBag;
            const newSalaryComponent: IPrPayslipEmploymentSalaryComponentEntity = {
                ...PassProps,
                ...getExpandedEnumProps(EntityTypeName.PrSalaryComponentType, entity?.TypeCode, "Type"),
                ...getExpandedEnumProps(EntityTypeName.PrRegularSalaryType, entity?.RegularSalaryTypeCode, "RegularSalaryType"),
                ...getExpandedEnumProps(EntityTypeName.PrFixedBonusTaxImpact, entity.CurrentTemporalPropertyBag?.FixedBonusTaxImpactCode, "FixedBonusTaxImpact"),
                ...getExpandedEnumProps(EntityTypeName.PrFixedBonusPayOutImpact, entity.CurrentTemporalPropertyBag?.FixedBonusPayOutImpactCode, "FixedBonusPayOutImpact"),
                ...getExpandedEnumProps(EntityTypeName.PrFixedBonusDeductionImpact, entity.CurrentTemporalPropertyBag?.FixedBonusDeductionImpact, "FixedBonusDeductionImpact"),
                PercentageOverride: Percentage,
                AmountOverride: Amount,
                IsOneTime: true,
                IsBasicSalary: IsBasicSalary
            };
            const path = salaryComponentsBc.getNavigationPath(false);
            addNewLineItem(storage, path, salaryComponentsGroupId, [], newSalaryComponent);
        });

        storage.refresh();
    };

    handleDocumentSelection = async (_: BindingContext[], entities: IDocumentEntity[], tableStorage: TSmartODataTableStorage): Promise<void> => {
        const { storage } = this.props;
        const current = storage.getValueByPath(PrPayslipEntity.LiabilitiesAndReceivables);
        const documentTypeCode = this._dialogType === DialogType.Receivables ? DocumentTypeCode.OtherReceivable : DocumentTypeCode.OtherLiability;
        const itemsOfOtherType = current.filter((item: IPrPayslipLiabilityReceivableEntity) => item.Document.DocumentTypeCode !== documentTypeCode);

        const { pairedItems } = (tableStorage as TSmartODataTableStorage<ISelectionWithEditableAmountCustomTableData>).getCustomData();

        const newItems: IPrPayslipLiabilityReceivableEntity[] = [];
        entities.forEach((entity, idx) => {
            const docType = entity.DocumentTypeCode;
            const entitySetName = getEntitySetByDocumentType(docType);
            const entityType = storage.oData.getMetadata().entitySets[entitySetName].getType();

            const currentItem = current.find((item: IPrPayslipLiabilityReceivableEntity) => item.Document.Id === entity.Id && item.Document.DocumentTypeCode === docType);
            const pairedItem = pairedItems[`${entitySetName}(${entity.Id})`];

            const pairedProps = {
                Amount: pairedItem.Amount,
                ExchangeRatePerUnit: pairedItem.ExchangeRatePerUnit,
                TransactionAmount: pairedItem.TransactionAmount,
            };

            if (!currentItem) {
                newItems.push(BindingContext.createNewEntity(current.length + idx, {
                    Document: {
                        Id: entity.Id,
                        _metadata: {
                            "": { type: `#${entityType.getFullName()}` }
                        },
                        Amount: entity.Amount,
                        NumberOurs: entity.NumberOurs,
                        ...getExpandedEnumProps(EntityTypeName.Currency, entity.CurrencyCode),
                        ...getExpandedEnumProps(EntityTypeName.DocumentType, docType),
                    },
                    ...getExpandedEnumProps(EntityTypeName.Currency, entity.CurrencyCode),
                    ...getExpandedEnumProps(EntityTypeName.Currency, pairedItem.TransactionCurrency?.Code, PrPayslipLiabilityReceivableEntity.TransactionCurrency),
                    ...pairedProps
                }));
            } else {
                newItems.push({
                    ...currentItem,
                    ...pairedProps
                });
            }
        });
        storage.setValueByPath(PrPayslipEntity.LiabilitiesAndReceivables, [...itemsOfOtherType, ...newItems]);
    };

    handleCloseDialog = (): void => {
        this._dialogType = null;
        this.forceUpdate();
    };

    getSelectionTableViewHeaderData = memoizeOne((): IReadOnlyListItem[] => {
        const employeeBc = this.props.storage.data.bindingContext.navigate(PrPayslipEntity.Employee);
        const employee = this.props.storage.getValue(employeeBc);
        const employmentTypeBc = this.currentEmploymentBc
            .navigate(PrPayslipEmploymentEntity.Employment)
            .navigate(PrEmploymentEntity.Type);
        const type: IPrEmploymentTypeEntity = this.props.storage.getValue(employmentTypeBc);
        return [
            {
                label: i18next.t("Payslip:Form.Employee"),
                value: universalPersonNameFormatter(employee)
            },
            {
                label: i18next.t("Payslip:Form.Type"),
                value: type?.Name
            }
        ];
    }, () => [this.props.storage.data.entity.Id]);

    handleEmailDialogClose = (alert?: IAlertProps): void => {
        const { storage } = this.props;

        storage.setCustomData({
            showEmailDialog: false
        });
        if (alert) {
            const { position, ...formAlertProps } = alert;
            storage.setFormAlert(formAlertProps);
            this.scrollPageUp();
        }
        this.forceUpdate();
    };

    renderCustomDialogs(): React.ReactElement {
        const { storage } = this.props;

        if (storage.getCustomData()?.showEmailDialog) {
            return (
                <SendPayslipByEmailDialog storage={storage}
                                          onClose={this.handleEmailDialogClose}/>
            );
        }

        let tableView;
        let getDef: IGetDefinition;
        let onSelection: (selected: BindingContext[], entities: IEntity[], tableStorage?: TSmartODataTableStorage) => Promise<void>;
        const restProps: Partial<ISelectionTableViewDialogProps> = {};

        const isCompensation = this.currentEmployment.Employment.TypeCode === PrEmploymentTypeCode.Compensation;
        const isDpp = this.currentEmployment.Employment.TypeCode === PrEmploymentTypeCode.WorkAgreementDPP;

        switch (this._dialogType) {
            case DialogType.SalaryComponent:
                tableView = PickSalaryComponentTableView;
                getDef = getPickSalaryComponentDefinitionsFactory(true, [], isCompensation, isDpp, this.entity.IsDelayedIncome);
                onSelection = this.handleSalaryComponentSelection;
                break;

            case DialogType.ExtraSalary:
                tableView = PickExtraSalaryTableView;
                getDef = getPickExtraSalaryDefinitionsFactory({
                    inEditableWindow: true,
                    // legally required extra salaries are by default generated by the system - they cannot be added manually
                    withoutLegallyRequired: true,
                });
                onSelection = this.handleExtraSalarySelection;
                break;

            case DialogType.Receivables:
            case DialogType.Liabilities:
                tableView = PayslipPairDocumentTableView;
                const entitySet = this._dialogType === DialogType.Receivables ? EntitySetName.OtherReceivables : EntitySetName.OtherLiabilities;
                getDef = getDefinitionsFactory(entitySet);
                onSelection = this.handleDocumentSelection;
                const documentTypeCode = this._dialogType === DialogType.Receivables ? DocumentTypeCode.OtherReceivable : DocumentTypeCode.OtherLiability;
                const pairedDocuments = this.props.storage.getValueByPath(PrPayslipEntity.LiabilitiesAndReceivables);
                const pairedItems: ISelectionWithEditableAmountCustomTableData["pairedItems"] = {};
                restProps.selectedItems = [];
                pairedDocuments.forEach((item: IPrPayslipLiabilityReceivableEntity) => {
                    if (item.Document.DocumentTypeCode === documentTypeCode) {
                        const key = `${entitySet}(${item.Document.Id})`;
                        pairedItems[key] = {
                            TransactionAmount: item.TransactionAmount,
                            Amount: item.Amount,
                            TransactionCurrency: item.TransactionCurrency,
                            ExchangeRatePerUnit: item.ExchangeRatePerUnit
                        };
                        restProps.selectedItems.push(this.props.storage.data.bindingContext.createNewBindingContext(key));
                    }
                });
                restProps.initializeStorage = (storage) => {
                    const tableStorage = (storage as unknown as TSmartODataTableStorage<ISelectionWithEditableAmountCustomTableData>);
                    tableStorage.setCustomData({ pairedItems });
                };
                break;
            case DialogType.Benefits:
                tableView = PickBenefitTableView;
                getDef = getPickBenefitDefinitionsFactory({ inEditableWindow: true });
                onSelection = this.handleBenefitSelection;
                break;
            default:
                // possible wrong configuration or no dialog configured
                return null;
        }
        return (
            <SelectionTableViewDialog storage={storage}
                                      tableView={tableView}
                                      getDefinition={getDef}
                                      title={storage.t("Payslip:Form.Title")}
                                      headerData={this.getSelectionTableViewHeaderData()}
                                      onSelection={onSelection}
                                      onClose={this.handleCloseDialog}
                                      {...restProps}
                                      isSingleSelect={false}/>
        );
    }

    onAfterSave(isNew: boolean, preventTableViewRefresh: boolean) {
        super.onAfterSave(isNew, preventTableViewRefresh);
    }

    onBeforeSave(data?: IPrPayslipEntity): IPrPayslipEntity {
        data = super.onBeforeSave(data) ?? data ?? cloneDeep(this.entity);
        // clear social insurance discount override value if it's same as original value and user did not explicitly
        // change it (it means that origEntity has the value undefined). Once user defines it manually, we keep user's value
        const hasEmployerSocialInsuranceDiscount = !data.SocialInsuranceDiscountIneligibilityReasonCode;
        if (isNotDefined(this.props.storage.data.origEntity.HasEmployerSocialInsuranceDiscountOverride)
            && hasEmployerSocialInsuranceDiscount === data.HasEmployerSocialInsuranceDiscountOverride) {
            data.HasEmployerSocialInsuranceDiscountOverride = null;
        }

        return this.clearNotOverriddenFields(this._overrideColumnPaths, data);
    }

    onSaveFail() {
        // if (...todo...) {
        // this.showAmountExceedingSalaryDialog();
        // }
        super.onSaveFail();
    }

    renderButtons(): React.ReactElement {
        const { storage } = this.props;
        if (this.isCustomerOverview) {
            return (
                <Button hotspotId={"formCancel"}
                        onClick={this.handleFormCancel}
                        isTransparent>{storage.t("Common:General.Cancel")}</Button>
            );
        }

        if (this.hasOpenAttendance) {
            return null;
        }

        const isFormReadOnly = storage.isReadOnly;
        const isSaveDisabled = this.shouldDisableSaveButton();
        const isCancelDisabled = this.shouldDisableCancelButton();

        return (
            <ButtonGroup wrap={"wrap"}>
                <Button hotspotId={"formCancel"}
                        isDisabled={!isFormReadOnly && (storage.isDisabled || isCancelDisabled)}
                        onClick={this.handleFormCancel}
                        isTransparent>{storage.t("Common:General.Cancel")}</Button>
                {this.isDeletable &&
                        <SmartFormDeleteButton storage={storage}
                                               onClick={this.handleDelete}/>
                    }
                {!isFormReadOnly && !this.entity.IsDelayedIncome && (
                        <Button hotspotId={"discardManualChanges"}
                                onClick={this.handleResetToOriginal}
                                isDisabled={isSaveDisabled /*ToDo: disable if there are no changes to discard -> BE flag needed */}
                                isTransparent>{storage.t(`Payslip:Form.${this.entity.MasterDataChanges?.length ? "RecalculateFromMasterData" : "DiscardManualChanges"}`)}</Button>
                    )}
                    {!isFormReadOnly &&
                            <Button hotspotId={"formSave"}
                                    onClick={this.handleSaveClick}
                                    isDisabled={isSaveDisabled}>
                                {storage.t("Payslip:Form.SaveAndRecalculate")}
                            </Button>
                    }
                </ButtonGroup>
        );
    }

    async showAmountExceedingSalaryDialog(): Promise<void> {
        const { storage } = this.props;

        // get newly calculated salary from error response
        // todo: get newly calculated salary from error response
        const salary = storage.data.alert?.detailData?.salary ?? 123;
        const message = i18next.t("Payslip:Form.AmountExceedingSalaryDialog", { salary: formatCurrency(salary, getCompanyCurrency(storage.context)) });

        // we are replacing standard error alert with confirmation dialog, where user can decide which action to take
        storage.setFormAlert(null);

        const shouldRemoveLinks = await this.props.confirmationDialog.open({
            content: message,
            dialogProps: {
                width: "416px"
            }
        });

        if (shouldRemoveLinks) {
            // user decided to remove links -> clear the list and try to save it again
            // ... todo...
        }
    }

    handleResetToOriginal = async (): Promise<void> => {
        const { storage } = this.props;
        storage.setBusy(true);
        const res = await resetPayslipToOriginal(this.entity.Id);
        if (res) {
            await storage.loadDataAfterSave();
            this.onAfterSave(false, false);
        } else {
            // show fail alert
            storage.setFormAlert({
                status: Status.Error,
                title: storage.t("Common:Errors.ErrorHappened"),
                subTitle: storage.t("Payslip:Form.ResetToOriginalFailed")
            });
            this.scrollPageUp();
        }
        storage.setBusy(false);
    };
}

export default withPermissionContext(withConfirmationDialog(PayslipFormView));
